import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as Config from "../api/config";
import Http from "../layout/Http";
import reviewDisplayColorPreset from "../config/reviewDisplayColorPreset";
import { flattenColor } from "../utils/colorConvert";

// Every named theme resolves from its preset; only "custom" (or unset / unknown)
// uses the saved palette. Same rule as resolveDisplayPalette in trustshop-fe
// (ReviewSubmissionForm/useEffectReviewSubmissionForm.js). Never overlay "custom":
// its preset entry holds defaults and would wipe the merchant's own colours.
export const resolveDisplayPalette = ({ color_mode, color_customize = {} }) => {
    if (!color_mode || color_mode === "custom") return color_customize;
    const preset = reviewDisplayColorPreset[color_mode];
    return preset ? { ...color_customize, ...preset } : color_customize;
};

// Shop's Review display palette as CSS variables for the portal root: every key
// of the resolved palette becomes `--<key>` (e.g. --primary_btn_bg_color), like
// the admin preview does. Pages, the write-review form and the after-review popup
// read them with their current colours as fallbacks.
// Uses the public (guest) settings endpoint so it also works before login.
const usePortalTheme = () => {
    const { shop_id } = useParams();
    const [themeStyle, setThemeStyle] = useState({});

    useEffect(() => {
        if (!shop_id) return;
        let cancelled = false;
        Http.get(
            `${Config.BASE_URL}/customer-management/guest/${shop_id}/settings/widget`
        )
            .then((res) => {
                const settings = res.data?.data;
                if (cancelled || !settings?.color_customize) return;
                const palette = resolveDisplayPalette(settings);
                const style = {};
                Object.entries(palette).forEach(([key, value]) => {
                    if (value) style[`--${key}`] = value;
                });
                // Popovers paint with the secondary button colour, which some
                // presets ship translucent: flatten it over the card it floats on
                // (same as the admin's --secondary_btn_bg_color_opaque).
                const secondaryOpaque = flattenColor(
                    palette.secondary_btn_bg_color,
                    palette.modal_detail_card_background
                );
                if (secondaryOpaque) {
                    style["--secondary_btn_bg_color_opaque"] = secondaryOpaque;
                }
                setThemeStyle(style);
            })
            .catch((err) => console.error("Error fetching portal theme:", err));
        return () => {
            cancelled = true;
        };
    }, [shop_id]);

    return themeStyle;
};

export default usePortalTheme;
